import React from 'react'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { Folder } from '@/types/bookmark'

interface FolderBreadcrumbProps {
  folder: Folder
  onFolderClick?: (folderId: string) => void
  className?: string
  maxDepth?: number
}

/**
 * 폴더 계층 구조를 빵부스러기(breadcrumb) 형태로 표시하는 컴포넌트
 * 예: "DCP > 영수증관리" 형태로 표시하며, 각 부모 폴더를 클릭하면 해당 폴더로 이동
 */
const FolderBreadcrumb: React.FC<FolderBreadcrumbProps> = ({
  folder,
  onFolderClick,
  className = '',
  maxDepth = 3,
}) => {
  // 폴더 계층 구조를 역순으로 추적하여 배열로 만들기
  const buildFolderPath = (currentFolder: Folder): Folder[] => {
    const path: Folder[] = []
    let currentPath = currentFolder
    let depth = 0

    while (currentPath && depth < maxDepth) {
      path.unshift(currentPath) // 앞쪽에 추가 (역순)
      currentPath = currentPath.parentFolder!
      depth++
    }

    return path
  }

  const folderPath = buildFolderPath(folder)

  // 경로가 너무 긴 경우 줄임표 처리
  const shouldTruncate = folderPath.length > maxDepth
  const displayPath = shouldTruncate ? folderPath.slice(-maxDepth) : folderPath

  if (folderPath.length === 0) {
    return null
  }

  return (
    <div className={`flex items-center text-sm ${className}`}>
      {/* 경로가 잘린 경우 줄임표 표시 */}
      {shouldTruncate && (
        <>
          <span className="text-gray-400 dark:text-gray-500">...</span>
          <ChevronRightIcon className="mx-1 h-3 w-3 text-gray-400 dark:text-gray-500" />
        </>
      )}

      {displayPath.map((pathFolder, index) => (
        <React.Fragment key={pathFolder._id}>
          {/* 폴더 아이템 */}
          <div className="flex items-center">
            {/* 폴더 색상 인디케이터 */}
            <div
              className="mr-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: pathFolder.color }}
            />

            {/* 폴더 이름 */}
            {onFolderClick ? (
              <button
                onClick={e => {
                  e.stopPropagation()
                  onFolderClick(pathFolder._id)
                }}
                className={`max-w-[100px] truncate transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${
                  index === displayPath.length - 1
                    ? 'font-medium text-gray-600 dark:text-gray-300'
                    : 'text-gray-500 hover:underline dark:text-gray-400'
                }`}
                title={pathFolder.name}
              >
                {pathFolder.name}
              </button>
            ) : (
              <span
                className={`max-w-[100px] truncate ${
                  index === displayPath.length - 1
                    ? 'font-medium text-gray-600 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                title={pathFolder.name}
              >
                {pathFolder.name}
              </span>
            )}
          </div>

          {/* 구분자 (마지막 항목이 아닌 경우) */}
          {index < displayPath.length - 1 && (
            <ChevronRightIcon className="mx-1.5 h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

export default FolderBreadcrumb
